import React from 'react';
import { FieldValues } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import type { SubsectionRouterProps } from '../../components/Router/SubsectionRouterProps';
import { SummaryAnswers } from '../../components/SummaryAnswers/SummaryAnswers';
import Tag from '../../components/Tag/Tag';
import { Question } from '../../config/types';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import type { FetchFormResponse } from '../../service/types';
import { Status } from '../../types';
import { isAdminPreviewMode, withAdminPreviewSearch } from '../../utils/admin-preview';
import {
  getConditionalChildRouting,
  getLockedSections,
  getLockedSubsections,
  getSelectedConditionalQuestions,
  isJsonObject,
  isPageCompleted,
  isSubsectionNotRequired,
  renderAnswer,
} from '../../utils/config-utils';
import { resolveSelectedFormId } from '../../utils/form-id';
import { hasValue } from '../../utils/form-utils';

function SummaryScreen({ subsection, sectionTitle, sectionName, urlPath }: SubsectionRouterProps) {
  const { formConfig, enabledSubsections, tenant } = useFormConfig();
  const tenantBasePath = tenant ? `/${tenant}` : '';
  const location = useLocation();
  const pages = enabledSubsections[subsection.slug]?.pages || subsection.pages || [];
  const allPages = subsection.pages || [];

  // Populated nil-return lists reopen their list editor; other list flows keep their summary.
  const navigate = useNavigate();
  const normalizedUrlPath = urlPath.replace(/^\/+/, '');
  const sectionSlug = normalizedUrlPath.split('/')[0];
  const overviewHash = `#section-${sectionSlug}`;
  const { data } = useFormData();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId,
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
    allowPendingAccessCheck: true,
  });
  const previewSearchSuffix = isPreviewMode ? '?admin=1' : '';
  const withPreviewSearch = (path: string) => withAdminPreviewSearch(path, isPreviewMode);
  const lockedSections = React.useMemo(
    () => getLockedSections(formConfig.sections, enabledSubsections, data),
    [data, enabledSubsections, formConfig.sections],
  );
  const lockedSubsections = React.useMemo(
    () => getLockedSubsections(formConfig.sections, enabledSubsections, data),
    [data, enabledSubsections, formConfig.sections],
  );
  const isCurrentSubsectionNotRequired = React.useMemo(
    () => isSubsectionNotRequired(subsection, data),
    [data, subsection],
  );
  const isCurrentRouteLocked =
    lockedSections[sectionSlug] === true ||
    lockedSubsections[subsection.slug] === true ||
    isCurrentSubsectionNotRequired;
  const role = String(data?.role || '').toLowerCase();
  const formState = String((data as any)?.formState || '').toLowerCase();
  const isReadOnlyViewMode =
    role === 'read_only' ||
    (role === 'admin' && !isPreviewMode) ||
    (role === 'approver' && formState === 'active');
  const answers = data?.answers || {};
  const conditionalRouting = React.useMemo(
    () =>
      getConditionalChildRouting(
        allPages,
        pages,
        data || {
          answers: {},
        },
        subsection.slug,
      ),
    [allPages, data, pages, subsection.slug],
  );
  const listPage = pages.find((p) => p.pageType === 'list');
  const listQuestions = listPage && 'questions' in listPage ? listPage.questions || [] : [];
  const hasListAnswers = listQuestions.some((question) =>
    Object.entries(answers).some(
      ([answerName, answer]) => answerName.endsWith(question.name) && hasValue(answer),
    ),
  );
  const draftsForSubsection = data?.drafts?.[subsection.slug] || [];
  const draftSummaryTargetPage = React.useMemo(
    () =>
      pages.find(
        (page) =>
          page.pageType === 'question' &&
          page.navigateToSummaryOnComplete === true &&
          draftsForSubsection.some((draftId) => draftId.split(':')[0] === page.name),
      ),
    [draftsForSubsection, pages],
  );
  const summaryOverviewLink = withPreviewSearch(`${tenantBasePath}${overviewHash}`);
  const summaryContinueLink = draftSummaryTargetPage
    ? withPreviewSearch(`../${draftSummaryTargetPage.name}`)
    : summaryOverviewLink;

  React.useEffect(() => {
    if (isCurrentRouteLocked) {
      navigate(withPreviewSearch(`${tenantBasePath}${overviewHash}`), { replace: true });
      return;
    }

    if (listPage?.type === 'nilreturn' && hasListAnswers && !isReadOnlyViewMode) {
      navigate(withPreviewSearch(`../${listPage.name}`), {
        replace: true,
        state: { showListSummary: true },
      });
      return;
    }

    if (listPage && hasListAnswers) {
      return;
    }

    if (conditionalRouting.hasChangeTypeButton && !isReadOnlyViewMode) {
      const targetPageName =
        conditionalRouting.controllerStatus !== Status.Completed
          ? conditionalRouting.controllerPageName
          : conditionalRouting.currentChildPageName;

      if (targetPageName) {
        navigate(withPreviewSearch(`../${targetPageName}`), { replace: true });
        return;
      }
    }
  }, [
    conditionalRouting.controllerPageName,
    conditionalRouting.controllerStatus,
    conditionalRouting.currentChildPageName,
    conditionalRouting.hasChangeTypeButton,
    hasListAnswers,
    isCurrentRouteLocked,
    isReadOnlyViewMode,
    listPage,
    navigate,
    overviewHash,
    tenantBasePath,
    previewSearchSuffix,
  ]);

  return (
    <PageTemplate
      breadcrumbs={{ sectionName, sectionTitle }}
      title={sectionTitle}
      subTitle={sectionName}
      fullWidth
    >
      <SummaryAnswers
        pages={pages}
        subsection={subsection}
        urlPath={urlPath}
        displayTitle
        data={data}
        disableChangeLinks={isReadOnlyViewMode}
        searchSuffix={previewSearchSuffix}
      />
      <div className="gov-uk-button-group">
        <Link
          className="govuk-button"
          data-module="govuk-button"
          data-prevent-double-click="true"
          to={summaryContinueLink}
        >
          Continue
        </Link>
        {!isReadOnlyViewMode && draftSummaryTargetPage && (
          <Link
            className="govuk-button govuk-button--secondary"
            data-module="govuk-button"
            data-prevent-double-click="true"
            to={summaryOverviewLink}
          >
            Save as unfinished
          </Link>
        )}
      </div>
    </PageTemplate>
  );
}

export default SummaryScreen;
